import React, { useState } from 'react';
import {
  Flex,
  Text,
  Box,
  Spinner,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  useClipboard,
  useToast,
} from '@chakra-ui/react';
import { MdOutlineRefresh, MdInfoOutline, MdContentCopy } from 'react-icons/md';
import { OrderStatusOptions } from 'config/constant';
import OrderApi from 'api/orders';

// Helper component for collapsible JSON
const JsonViewer = ({ data, level = 0 }) => {
  if (data === null) return <span style={{ color: 'gray' }}>null</span>;
  if (data === undefined) return <span style={{ color: 'gray' }}>undefined</span>;
  if (typeof data === 'boolean') return <span style={{ color: 'blue' }}>{data.toString()}</span>;
  if (typeof data === 'number') return <span style={{ color: 'darkgreen' }}>{data}</span>;
  if (typeof data === 'string') return <span style={{ color: '#c41d7f' }}>"{data}"</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span>[]</span>;
    return (
      <details style={{ marginLeft: level * 10 + 'px' }} open>
        <summary style={{ cursor: 'pointer', outline: 'none', color: 'purple' }}>
          [{data.length}]
        </summary>
        <div style={{ paddingLeft: '10px' }}>
          {data.map((item, index) => (
            <div key={index}>
              <span style={{ color: 'gray', marginRight: '5px' }}>{index}:</span>
              <JsonViewer data={item} level={level + 1} />
            </div>
          ))}
        </div>
      </details>
    );
  }

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 0) return <span>{'{ }'}</span>;
    return (
      <details style={{ marginLeft: level * 10 + 'px' }} open>
        <summary style={{ cursor: 'pointer', outline: 'none', color: '#005cc5' }}>
          {'{...}'}
        </summary>
        <div style={{ paddingLeft: '10px' }}>
          {keys.map((key) => (
            <div key={key} style={{ display: 'flex', alignItems: 'flex-start' }}>
              <strong style={{ marginRight: '5px', color: '#000' }}>{key}:</strong>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      </details>
    );
  }

  return <span>{String(data)}</span>;
};

const TransactionStatus = (props) => {
  const status = props.status;
  const orderStatus = props.orderStatus;
  const [verifying, setVerifying] = useState(false);
  const [checkStatusLoading, setCheckStatusLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const RefreshOrderData = props.propFunct;
  const textColor = useColorModeValue('gray.700', 'white');
  let userType = null;
  try {
    const sessionUser = sessionStorage.getItem('user');
    if (sessionUser && sessionUser !== 'undefined') {
      userType = JSON.parse(sessionUser)?.user_type;
    }
  } catch (e) {
    console.error('Error parsing user from session storage', e);
  }
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const handleCopy = (data) => {
    const isJuspay = data?.transaction?.payment_source === 'JUSPAY';
    let details = '';

    if (isJuspay) {
      details = `
Transaction Details:
txnId: ${data?.transaction?.id || ''}
merchantId: ${data?.payment_response?.payload?.merchantId || ''}
totalamount: ${data?.transaction?.amount || ''}

Payment Response:
status: ${data?.payment_response?.status || ''}
responseCode: ${data?.payment_response?.responseCode || ''}
responseMessage: ${data?.payment_response?.responseMessage || ''}
gatewayResponseStatus: ${data?.payment_response?.payload?.gatewayResponseStatus || ''}
gatewayResponseMessage: ${data?.payment_response?.payload?.gatewayResponseMessage || ''}
gatewayTransactionId: ${data?.payment_response?.payload?.gatewayTransactionId || ''}
payerVpa: ${data?.payment_response?.payload?.payerVpa || ''}
      `.trim();
    } else {
      details = `
Transaction Details:
txnId: ${data?.transaction?.details?.payment_gateway_details?.txnId || ''}
consumerId: ${data?.transaction?.details?.payment_gateway_details?.consumerId || ''}
merchantId: ${data?.transaction?.details?.payment_gateway_details?.merchantId || ''}
totalamount: ${data?.transaction?.details?.payment_gateway_details?.totalamount || ''}
consumerEmailId: ${data?.transaction?.details?.payment_gateway_details?.consumerEmailId || ''}
consumerMobileNo: ${data?.transaction?.details?.payment_gateway_details?.consumerMobileNo || ''}

Payment Response:
identifier: ${data?.payment_response?.paymentMethod?.paymentTransaction?.identifier || ''}
statusCode: ${data?.payment_response?.paymentMethod?.paymentTransaction?.statusCode || ''}
statusMessage: ${data?.payment_response?.paymentMethod?.paymentTransaction?.statusMessage || ''}
errorMessage: ${data?.payment_response?.paymentMethod?.paymentTransaction?.errorMessage || ''}
bankSelectionCode: ${data?.payment_response?.paymentMethod?.bankSelectionCode || ''}
      `.trim();
    }

    navigator.clipboard.writeText(details).then(() => {
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    });
  };

  const VerifyTransaction = async (id) => {
    try {
      setVerifying(true);
      let response = await OrderApi.Verify({ transactionId: id });
      response = response.data;
      if (response.status_code === 200) {
        setVerifying(false);
        RefreshOrderData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const CheckStatus = async (id) => {
    try {
      setCheckStatusLoading(true);
      setApiResponse(null);
      let response = await OrderApi.CheckStatus({ transactionId: id });
      setApiResponse(response.data);
      setCheckStatusLoading(false);
      onOpen();
    } catch (err) {
      console.log(err);
      setCheckStatusLoading(false);
    }
  };

  return (
    <Flex align='center'>
      {status !== 'PENDING' &&
        !(orderStatus == 'FAILED' && status == 'COMPLETED') ? (
        <Flex align='center'>
          <Text
            me='10px'
            fontSize='sm'
            fontWeight='700'
            color={
              status === 'COMPLETED'
                ? 'green'
                : status === 'INACTIVE'
                  ? 'red'
                  : { textColor }
            }>
            {OrderStatusOptions.find((option) => option.value === status)
              ?.label}
          </Text>
        </Flex>
      ) : (
        <Box
          as='button'
          display='flex'
          variant='light'
          onClick={() => VerifyTransaction(props.id)}>
          <Text
            me='5px'
            fontSize='sm'
            fontWeight='700'
            color={
              status === 'COMPLETED'
                ? 'green'
                : status === 'INACTIVE'
                  ? 'red'
                  : { textColor }
            }>
            {OrderStatusOptions.find((option) => option.value === status)
              ?.label}
          </Text>
          {verifying ? <Spinner size='xs' /> : <MdOutlineRefresh />}
        </Box>
      )}

      {userType === 'SUPER_ADMIN' && (
        <Box
          as='button'
          ms='10px'
          display='flex'
          variant='light'
          onClick={() => CheckStatus(props.id)}
          title="Check Transaction Status">
          {checkStatusLoading ? <Spinner size='xs' /> : <MdInfoOutline />}
        </Box>
      )}

      {props.paymentMethod === 'PAYMENT_GATEWAY' && (
        <Button
          ms='10px'
          variant='brand'
          size='sm'
          onClick={() => CheckStatus(props.id)}
          isLoading={checkStatusLoading}
          leftIcon={<MdInfoOutline />}>
          Get Details
        </Button>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Transaction Status Check</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              bg="gray.50"
              p={4}
              borderRadius="md"
              overflow="auto"
              maxH="500px"
              fontSize="sm"
              border="1px solid"
              borderColor="gray.200"
            >
              {props.paymentMethod === 'PAYMENT_GATEWAY' && apiResponse ? (
                <Box>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="bold">Transaction Details:</Text>
                    <Button
                      size="xs"
                      leftIcon={<MdContentCopy />}
                      onClick={() => handleCopy(apiResponse?.data || apiResponse)}
                      colorScheme="brand"
                      variant="outline"
                    >
                      Copy Details
                    </Button>
                  </Flex>
                  <Box pl={4} mb={4}>
                    {(() => {
                      const data = apiResponse?.data || apiResponse;
                      const isJuspay = data?.transaction?.payment_source === 'JUSPAY';

                      if (isJuspay) {
                        return (
                          <>
                            <Text><b>txnId :</b> {data?.transaction?.id}</Text>
                            <Text><b>merchantId :</b> {data?.payment_response?.payload?.merchantId}</Text>
                            <Text><b>totalamount :</b> {data?.transaction?.amount}</Text>
                          </>
                        );
                      }

                      return (
                        <>
                          <Text><b>txnId :</b> {data?.transaction?.details?.payment_gateway_details?.txnId}</Text>
                          <Text><b>consumerId :</b> {data?.transaction?.details?.payment_gateway_details?.consumerId}</Text>
                          <Text><b>merchantId :</b> {data?.transaction?.details?.payment_gateway_details?.merchantId}</Text>
                          <Text><b>totalamount :</b> {data?.transaction?.details?.payment_gateway_details?.totalamount}</Text>
                          <Text><b>consumerEmailId :</b> {data?.transaction?.details?.payment_gateway_details?.consumerEmailId}</Text>
                          <Text><b>consumerMobileNo :</b> {data?.transaction?.details?.payment_gateway_details?.consumerMobileNo}</Text>
                        </>
                      );
                    })()}
                  </Box>

                  <Text fontWeight="bold" mb={2}>Payment Response:</Text>
                  <Box pl={4}>
                    {(() => {
                      const data = apiResponse?.data || apiResponse;
                      const isJuspay = data?.transaction?.payment_source === 'JUSPAY';

                      if (isJuspay) {
                        return (
                          <>
                            <Text><b>status :</b> {data?.payment_response?.status}</Text>
                            <Text><b>responseCode :</b> {data?.payment_response?.responseCode}</Text>
                            <Text><b>responseMessage :</b> {data?.payment_response?.responseMessage}</Text>
                            <Text><b>gatewayResponseStatus :</b> {data?.payment_response?.payload?.gatewayResponseStatus}</Text>
                            <Text><b>gatewayResponseMessage :</b> {data?.payment_response?.payload?.gatewayResponseMessage}</Text>
                            <Text><b>gatewayTransactionId :</b> {data?.payment_response?.payload?.gatewayTransactionId}</Text>
                            <Text><b>payerVpa :</b> {data?.payment_response?.payload?.payerVpa}</Text>
                          </>
                        );
                      }

                      return (
                        <>
                          <Text><b>identifier :</b> {data?.payment_response?.paymentMethod?.paymentTransaction?.identifier}</Text>
                          <Text><b>statusCode :</b> {data?.payment_response?.paymentMethod?.paymentTransaction?.statusCode}</Text>
                          <Text><b>statusMessage :</b> {data?.payment_response?.paymentMethod?.paymentTransaction?.statusMessage}</Text>
                          <Text><b>errorMessage :</b> {data?.payment_response?.paymentMethod?.paymentTransaction?.errorMessage}</Text>
                          <Text><b>bankSelectionCode :</b> {data?.payment_response?.paymentMethod?.bankSelectionCode}</Text>
                        </>
                      );
                    })()}
                  </Box>

                  {userType === 'SUPER_ADMIN' && (
                    <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.200">
                      <Text fontWeight="bold" mb={2}>Raw JSON Response (Super Admin Only):</Text>
                      <JsonViewer data={apiResponse} />
                    </Box>
                  )}
                </Box>
              ) : (
                <JsonViewer data={apiResponse} />
              )}
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default TransactionStatus;
